import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRenderStore } from '../model/render-form.store';
import { useJobQueueStore } from '@/features/design/job-queue';
import { rendersService } from '@/shared/api/services/renders.service';
import { cacheInvalidation, cacheKeys } from '@/shared/lib/cache-invalidation';
import { toast } from '@/shared/lib/toast';
import type { RenderCreateRequest, Render } from '@/types/render';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  upsertRender,
  removeRenderFromCache,
} from '../model/renders-cache';

/**
 * Hook for render CRUD operations using TanStack Query
 */
export function useRenderOperations() {
  const queryClient = useQueryClient();
  const t = useTranslations('render.options');
  const tCommon = useTranslations('common');
  const setSelectedRender = useRenderStore((state) => state.setSelectedRender);
  const openRenderDetails = useRenderStore((state) => state.openRenderDetails);

  // Create render mutation. No lockId — render id isn't known until the
  // server response, and there's no existing record to protect from a WS
  // event during creation.
  const createRenderMutation = useMutation({
    mutationKey: ['renders', 'create'],
    mutationFn: async ({
      conceptId,
      conceptAngleId,
      data,
    }: {
      conceptId: string;
      conceptAngleId: string;
      data: RenderCreateRequest;
    }) => {
      return rendersService.createRender(conceptId, data, conceptAngleId);
    },
    onSuccess: (render, variables) => {
      // Hydrate Query cache immediately for instant UI updates — render
      // entity owned by Query (STATE_POLICY); job-queue store keeps only
      // UI job state.
      upsertRender(render, variables.conceptId);

      const store = useJobQueueStore.getState();

      // Add pending render job for instant UI feedback (before WebSocket snapshot)
      const now = new Date().toISOString();
      store.addPendingRenderJob({
        id: render.id,
        jobType: 'render',
        status: render.attributes.status || 'pending',
        startedAt: render.attributes.started_at || null,
        durationPredicted: render.attributes.duration_predicted || 60,
        attemptNumber: 1,
        createdAt: render.attributes.created_at || now,
        updatedAt: render.attributes.updated_at || now,
        completedAt: render.attributes.completed_at || null,
        conceptId:
          render.relationships?.concept_angle?.meta?.concept_id ||
          variables.conceptId,
        projectId: '',
        conceptName: null,
        conceptSequenceNumber: 0,
        projectName: '',
        conceptAngleId:
          render.relationships?.concept_angle?.data?.id ||
          variables.conceptAngleId,
        angleType: 'main', // Safe default — WebSocket snapshot will correct if derivative
        scenarioType: render.attributes.render_type,
        imageUrl: null,
        angleUrl: null,
      });

      // Invalidate concept detail to update render settings
      cacheInvalidation.afterUpdateConcept(variables.conceptId);

      // Invalidate job-queue for queue metadata sync
      queryClient.invalidateQueries({ queryKey: cacheKeys.jobQueue.all });
    },
  });

  // Delete render mutation with undo support
  const deleteRenderMutation = useMutation({
    mutationKey: ['renders', 'delete'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (renderId: string) => {
      return rendersService.deleteRender(renderId);
    },
    onSuccess: (_, renderId) => {
      // Remove from Query cache (list + detail across all concepts).
      removeRenderFromCache(renderId);

      // Invalidate job-queue for queue metadata sync
      queryClient.invalidateQueries({ queryKey: cacheKeys.jobQueue.all });

      // Invalidate job history so Recent section updates
      queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });

      // Show undo toast
      toast.undoable(t('renderDeleted'), {
        action: {
          label: tCommon('undo'),
          onClick: async () => {
            try {
              await rendersService.restoreRender(renderId);
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : t('failedToRestoreRender')
              );
            }
            queryClient.invalidateQueries({ queryKey: cacheKeys.jobQueue.all });
            queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
          },
        },
      });
    },
  });

  // Update render mutation
  const updateRenderMutation = useMutation({
    mutationKey: ['renders', 'update'],
    meta: { lockId: (vars: unknown) => (vars as { renderId: string }).renderId },
    mutationFn: async ({
      renderId,
      updates,
    }: {
      renderId: string;
      updates: Partial<Render['attributes']>;
    }) => {
      return rendersService.updateRender(renderId, updates);
    },
    onSuccess: (render) => {
      const conceptId = render.relationships?.concept_angle?.meta?.concept_id;

      // Update render in Query cache (list + detail).
      upsertRender(render, conceptId);

      // Invalidate concept to update render settings
      if (conceptId) {
        cacheInvalidation.afterUpdateConcept(conceptId);
      }

      // Invalidate job-queue for queue metadata sync
      queryClient.invalidateQueries({ queryKey: cacheKeys.jobQueue.all });
    },
  });

  // Callback functions
  const createRender = useCallback(
    async (
      conceptId: string,
      conceptAngleId: string,
      data: RenderCreateRequest
    ): Promise<Render> => {
      return createRenderMutation.mutateAsync({
        conceptId,
        conceptAngleId,
        data,
      });
    },
    [createRenderMutation]
  );

  const deleteRender = useCallback(
    async (renderId: string): Promise<void> => {
      return deleteRenderMutation.mutateAsync(renderId);
    },
    [deleteRenderMutation]
  );

  const updateRender = useCallback(
    async (
      renderId: string,
      updates: Partial<Render['attributes']>
    ): Promise<Render> => {
      return updateRenderMutation.mutateAsync({ renderId, updates });
    },
    [updateRenderMutation]
  );

  const selectRender = useCallback(
    (renderId: string | null) => {
      setSelectedRender(renderId);
      if (renderId) {
        openRenderDetails(renderId);
      }
    },
    [setSelectedRender, openRenderDetails]
  );

  return {
    // Mutations
    createRenderMutation,
    deleteRenderMutation,
    updateRenderMutation,

    // Callbacks
    createRender,
    deleteRender,
    updateRender,
    selectRender,

    // Loading states from mutations
    isCreating: createRenderMutation.isPending,
    isDeleting: deleteRenderMutation.isPending,
    isUpdating: updateRenderMutation.isPending,

    // Errors from mutations
    createError: createRenderMutation.error,
    deleteError: deleteRenderMutation.error,
    updateError: updateRenderMutation.error,
  };
}
